import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { latestAttr, type AttrMap } from '../lib/inlineAttribution';
import { roleLabel } from '../lib/roles';
import { Badge, Button } from '../atoms';
import { InlineEditCard, EntityGroupsPanel, LastUpdated } from '../organisms';
import { EntityHeader } from '../shell';

/**
 * UserDetail — /users/:id  (route stays wrapped in RequireSuperAdmin)
 *
 * Bespoke card profile for a single user, modeled on ClientDetail's OverviewTab:
 *   - Account       — email + status, EDITABLE in place via PUT /api/users/:id.
 *   - Role & access — role + security group, READ-ONLY (both are PROTECTED_COLUMNS
 *                     on the generic users PUT — they can only move through their
 *                     dedicated super-admin paths, never here).
 *   - Portal identity — which CONTACT a portal user is, shown for portal users ONLY.
 *
 * ⚠️ Q8/E6 (Yadira, round 5): *"keep the link in the data, drop it from the UI —
 * remove the linked contact section from INTERNAL user profiles only. On a client
 * portal user profile, showing which contact they are is correct and stays."*
 * `users.contact_id` is load-bearing and always persists: it is how a staff user's
 * book resolves (`parcels.consultant_id = user.contact_id`) and how a portal login
 * traces back to a person, so access can be revoked when the contact goes away.
 * What is scoped is the DISPLAY — an internal profile deliberately shows nothing
 * here, because for staff the link is plumbing rather than something an admin sets
 * on this screen. Do not add a contact row to the internal cards.
 *
 * Per-section attribution comes from GET /api/field-attribution/users/:id when it
 * resolves; a failure just omits the timestamps (the section still renders).
 */

interface User {
  id: string;
  email: string;
  role: string;
  security_group_id: string | null;
  status: 'active' | 'inactive' | 'locked' | 'pending_invite';
  contact_id: string;
  client_id: string | null;
  last_login_at?: string | null;
  updated_at?: string | null;
}

interface ContactLite {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
}

interface SecurityGroupLite {
  id: string;
  name: string | null;
  base_role: string | null;
}

const statusVariant: Record<User['status'], 'success' | 'neutral' | 'danger' | 'warning'> = {
  active: 'success', inactive: 'neutral', locked: 'danger', pending_invite: 'warning',
};
const statusLabel: Record<User['status'], string> = {
  active: 'Active', inactive: 'Inactive', locked: 'Locked', pending_invite: 'Pending invite',
};

export function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const userQuery = useQuery<User>({
    queryKey: ['users', id],
    queryFn: ({ signal }) => api.get<User>(`/api/users/${id}`, signal),
    enabled: !!id,
  });


  if (userQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading user…</p>;
  }

  if (userQuery.error) {
    const notFound = userQuery.error instanceof ApiClientError && userQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'User not found' : 'Failed to load user'}</h2>
        <p style={{ margin: 0 }}>{(userQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/users')}>
            Back to Users
          </Button>
        </p>
      </div>
    );
  }

  if (!userQuery.data) return null;

  const user = userQuery.data;

  return (
    <>
      <EntityHeader
        prefix="User"
        title={user.email}
        subtitle={<>
          <span style={{ overflowWrap: 'anywhere' }}>{user.email}</span>
          <LastUpdated updatedBy={undefined} updatedAt={user.updated_at} />
        </>}
        statusPills={<>
          <Badge variant="neutral">{roleLabel(user.role)}</Badge>
          <Badge variant={statusVariant[user.status]} dot>{statusLabel[user.status]}</Badge>
        </>}
        actions={
          <Button variant="secondary" size="sm" onClick={() => navigate('/users')}>
            Back
          </Button>
        }
      />

      <OverviewCards user={user} />

      <div style={{ marginTop: 'var(--sp-5)' }}>
        <EntityGroupsPanel entityType="user" entityId={user.id} />
      </div>
    </>
  );
}

function OverviewCards({ user }: { user: User }) {
  // Portal users only. The contact is fetched ONLY when it will be rendered, so
  // an internal profile issues no request for data it must not show; `enabled`
  // keeps the hook unconditional (Rules of Hooks) while doing that.
  const isPortalUser = user.role === 'client';
  const contactQuery = useQuery<ContactLite>({
    queryKey: ['contact', user.contact_id],
    queryFn: ({ signal }) => api.get<ContactLite>(`/api/contacts/${user.contact_id}`, signal),
    enabled: isPortalUser && !!user.contact_id,
  });

  // Per-FIELD attribution (email/status). users audit with entity_type='users', so
  // this resolves for the super_admin who reaches the page. On any error the query
  // simply yields no data → the attribution line is omitted (never a crash).
  const attrQuery = useQuery<AttrMap>({
    queryKey: ['field-attribution', 'users', user.id],
    queryFn: ({ signal }) => api.get<AttrMap>(`/api/field-attribution/users/${user.id}`, signal),
    retry: false,
  });
  const attr = attrQuery.data ?? {};

  // Resolve the security group name for the read-only Role & access card.
  const groupQuery = useQuery<SecurityGroupLite>({
    queryKey: ['security-groups', user.security_group_id],
    queryFn: ({ signal }) => api.get<SecurityGroupLite>(`/api/security-groups/${user.security_group_id}`, signal),
    enabled: !!user.security_group_id,
  });
  const groupName = user.security_group_id ? (groupQuery.data?.name || '—') : '—';

  const record = user as unknown as Record<string, unknown>;
  const invalidate: unknown[][] = [['users', user.id], ['users-list'], ['field-attribution', 'users', user.id]];

  // The route is super-admin-only (RequireSuperAdmin), and super_admin always
  // authorizes a users PUT server-side, so the Account card is editable. ('user'
  // isn't in the permission grid, so useEntityPermission would wrongly deny it.)
  const canEditAccount = true;

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'locked', label: 'Locked' },
  ];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
      gap: 'var(--sp-4)',
    }}>
      <InlineEditCard
        title="Account"
        routePath="users"
        recordId={user.id}
        record={record}
        canEdit={canEditAccount}
        invalidateKeys={invalidate}
        attribution={latestAttr(attr, ['email', 'status'])}
        fields={[
          { key: 'email', label: 'Email', type: 'text', required: true },
          { key: 'status', label: 'Status', type: 'select', options: statusOptions, display: (v) => statusLabel[String(v) as User['status']] ?? String(v ?? '—') },
        ]}
      />

      {/* Role & access — read-only. role + security_group_id are PROTECTED_COLUMNS
          on the generic users PUT, so they are display-only here (all-readOnly →
          the card shows no Edit affordance). */}
      <InlineEditCard
        title="Role & access"
        routePath="users"
        recordId={user.id}
        record={record}
        canEdit={false}
        fields={[
          { key: 'role', label: 'Role', readOnly: true, display: (v) => <Badge variant="neutral">{roleLabel(String(v))}</Badge> },
          { key: 'security_group_id', label: 'Security group', readOnly: true, display: () => groupName },
        ]}
      />

      {/* Portal identity — Q8: shown for a portal user, never for internal staff.
          The link itself is stored either way; only this display is scoped. */}
      {isPortalUser && (
        <InlineEditCard
          title="Portal identity"
          routePath="users"
          recordId={user.id}
          record={record}
          canEdit={false}
          fields={[
            {
              key: 'contact_id',
              label: 'Signs in as',
              readOnly: true,
              display: () => {
                const c = contactQuery.data;
                if (!c) return user.contact_id ? '—' : 'No contact linked';
                const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.company_name;
                return name || c.id.slice(0, 8);
              },
            },
          ]}
        />
      )}
    </div>
  );
}
